import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

interface DashboardData {
  summary: {
    totalTickets: number;
    totalCases: number;
    totalIncidents: number;
    totalServiceRequests: number;
    totalChanges: number;
    totalProblems: number;
    slaCompliance: number;
    slaMet: number;
    slaBreached: number;
    slaBreachedPercent?: number;
    totalSlaApplied?: number;
  };
  byTicketType: {
    CASE?: { total: number };
    INCIDENT: { total: number };
    SERVICE_REQUEST: { total: number };
    CHANGE: { total: number };
    PROBLEM: { total: number };
  };
  byState: {
    new: number;
    in_progress: number;
    on_hold: number;
    resolved: number;
    closed: number;
    cancelled: number;
  };
  byPriority: {
    critical_1: number;
    high_2: number;
    moderate_3: number;
    low_4: number;
    planning_5: number;
  };
  trend: Array<{
    date: string;
    cases: number;
    incidents: number;
    serviceRequests: number;
    changes: number;
    problems: number;
  }>;
}

interface MatrixRow {
  customerId: string;
  customerName: string;
  data: { groupId: string; groupName: string; count: number }[];
}

interface MatrixData {
  customers: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  matrix: MatrixRow[];
  maxCount: number;
}

interface CapturedImage {
  dataUrl: string;
  width: number;
  height: number;
  aspectRatio: number;
}

// Capture a DOM element as image with dimensions
const captureElementAsImage = async (elementId: string): Promise<CapturedImage | null> => {
  const element = document.getElementById(elementId);
  if (!element) return null;

  try {
    // Get the actual rendered dimensions
    const rect = element.getBoundingClientRect();

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
    });

    return {
      dataUrl: canvas.toDataURL('image/png'),
      width: rect.width,
      height: rect.height,
      aspectRatio: rect.width / rect.height,
    };
  } catch (error) {
    console.error(`Failed to capture element ${elementId}:`, error);
    return null;
  }
};

// Generate insights based on data analysis
const generateInsights = (data: DashboardData): string[] => {
  const insights: string[] = [];
  const total = data.summary.totalTickets;

  // SLA Analysis
  const slaBreachPercent = data.summary.slaBreachedPercent || 0;
  if (slaBreachPercent > 30) {
    insights.push(`Critical: SLA breach rate is ${slaBreachPercent.toFixed(1)}% - immediate attention required to improve service delivery.`);
  } else if (slaBreachPercent > 10) {
    insights.push(`Warning: SLA breach rate of ${slaBreachPercent.toFixed(1)}% is above acceptable threshold. Review resource allocation.`);
  } else if (slaBreachPercent <= 10 && data.summary.totalSlaApplied && data.summary.totalSlaApplied > 0) {
    insights.push(`Positive: Excellent SLA compliance at ${data.summary.slaCompliance.toFixed(1)}%. Maintain current service levels.`);
  }

  // Incident Analysis
  if (total > 0) {
    const incidentPercent = (data.summary.totalIncidents / total) * 100;
    if (incidentPercent > 40) {
      insights.push(`High incident volume (${incidentPercent.toFixed(1)}% of tickets). Consider proactive problem management.`);
    }
  }

  // State Distribution Analysis
  const onHoldTickets = data.byState.on_hold;
  if (total > 0 && (onHoldTickets / total) > 0.2) {
    insights.push(`${onHoldTickets} tickets (${((onHoldTickets / total) * 100).toFixed(1)}%) are on hold. Review blockers and dependencies.`);
  }

  if (data.byState.new > 10) {
    insights.push(`${data.byState.new} new tickets pending assignment. Consider workload distribution across teams.`);
  }

  // Priority Analysis
  const criticalHigh = data.byPriority.critical_1 + data.byPriority.high_2;
  if (criticalHigh > 0 && total > 0) {
    const criticalHighPercent = (criticalHigh / total) * 100;
    if (criticalHighPercent > 30) {
      insights.push(`${criticalHighPercent.toFixed(1)}% of tickets are Critical/High priority. Ensure adequate senior resource allocation.`);
    }
  }

  // Trend Analysis
  if (data.trend.length >= 7) {
    const recentWeek = data.trend.slice(-7);
    const totalRecent = recentWeek.reduce((sum, d) => sum + d.incidents + d.serviceRequests + d.changes + d.problems + (d.cases || 0), 0);
    const avgDaily = totalRecent / 7;
    if (avgDaily > 20) {
      insights.push(`High ticket volume trend: averaging ${avgDaily.toFixed(0)} tickets per day. Consider capacity planning.`);
    }
  }

  return insights.length > 0 ? insights : ['No significant issues detected. Operations are running smoothly.'];
};

// Generate recommended actions based on data
const generateRecommendations = (data: DashboardData): string[] => {
  const recommendations: string[] = [];

  // SLA Recommendations
  const slaBreachPercent = data.summary.slaBreachedPercent || 0;
  if (slaBreachPercent > 20) {
    recommendations.push('Conduct root cause analysis on SLA breaches to identify systemic issues.');
    recommendations.push('Consider implementing automated escalation for tickets approaching SLA deadlines.');
  }

  // State-based Recommendations
  if (data.byState.new > 5) {
    recommendations.push(`Assign pending ${data.byState.new} new tickets to available agents within 4 hours.`);
  }

  if (data.byState.on_hold > 3) {
    recommendations.push(`Review ${data.byState.on_hold} on-hold tickets for unresolved dependencies or customer responses.`);
  }

  // Priority-based Recommendations
  if (data.byPriority.critical_1 > 0) {
    recommendations.push(`Prioritize resolution of ${data.byPriority.critical_1} Critical tickets with dedicated resources.`);
  }

  // Problem Management
  if (data.summary.totalProblems > 5) {
    recommendations.push('Schedule problem review meeting to accelerate root cause resolution.');
  }

  // Incident Prevention
  if (data.summary.totalIncidents > 20) {
    recommendations.push('Analyze recurring incidents for potential problem ticket creation and permanent fixes.');
  }

  // Change Management
  if (data.summary.totalChanges > 10) {
    recommendations.push('Ensure CAB review schedule accommodates pending change requests.');
  }

  return recommendations.length > 0 ? recommendations : ['Continue monitoring and maintain current operational practices.'];
};

export const generateDashboardPDF = async (
  data: DashboardData,
  customerName: string,
  durationLabel: string,
  groupNames: string[],
  matrixData?: MatrixData
): Promise<void> => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = 15;

  // Colors
  const primaryColor: [number, number, number] = [30, 64, 175]; // Blue
  const successColor: [number, number, number] = [16, 185, 129]; // Green
  const dangerColor: [number, number, number] = [239, 68, 68]; // Red
  const warningColor: [number, number, number] = [245, 158, 11]; // Amber
  const grayColor: [number, number, number] = [107, 114, 128];

  // Helper functions
  const checkNewPage = (requiredSpace: number) => {
    if (yPos + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
    }
  };

  const drawSectionHeader = (title: string) => {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(title, margin, yPos);
    yPos += 2;
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
  };

  // ========== COMPACT HEADER WITH LOGO ==========

  // Logo placeholder
  doc.setFillColor(30, 64, 175);
  doc.roundedRect(margin, yPos, 22, 8, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('TENARAI', margin + 2.5, yPos + 5.5);

  // Application Name
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Pulse', margin + 26, yPos + 6);

  // Report Type
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  doc.text('Customer Dashboard Report', margin + 48, yPos + 6);

  // Generated date on the right
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin, yPos + 6, { align: 'right' });

  yPos += 12;

  // Horizontal line
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  // ========== COMPACT FILTER INFORMATION ==========

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  const filterBoxHeight = groupNames.length > 0 ? 12 : 10;
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, filterBoxHeight, 2, 2, 'FD');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Filters:', margin + 3, yPos + 5);

  doc.setFont('helvetica', 'normal');
  doc.text(`Customer: ${customerName}`, margin + 22, yPos + 5);
  doc.text(`Period: ${durationLabel}`, margin + 85, yPos + 5);
  if (groupNames.length > 0) {
    doc.text(`Groups: ${groupNames.slice(0, 3).join(', ')}${groupNames.length > 3 ? '...' : ''}`, margin + 3, yPos + 10);
  }
  yPos += filterBoxHeight + 4;

  // ========== COMPACT EXECUTIVE SUMMARY ==========

  // Section title inline (no separate header)
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Summary', margin, yPos);
  yPos += 5;

  // Summary cards - more compact
  const cardData = [
    { label: 'Total', value: data.summary.totalTickets, color: grayColor },
    { label: 'Cases', value: data.summary.totalCases || 0, color: [147, 51, 234] as [number, number, number] },
    { label: 'Incidents', value: data.summary.totalIncidents, color: dangerColor },
    { label: 'SRs', value: data.summary.totalServiceRequests, color: primaryColor },
    { label: 'Changes', value: data.summary.totalChanges, color: successColor },
    { label: 'Problems', value: data.summary.totalProblems, color: warningColor },
  ];

  const cardWidth = (pageWidth - margin * 2 - 15) / 6;
  const cardHeight = 16;
  cardData.forEach((card, i) => {
    const x = margin + i * (cardWidth + 3);
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(229, 231, 235);
    doc.roundedRect(x, yPos, cardWidth, cardHeight, 2, 2, 'FD');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...card.color);
    doc.text(String(card.value), x + cardWidth / 2, yPos + 8, { align: 'center' });

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayColor);
    doc.text(card.label, x + cardWidth / 2, yPos + 13, { align: 'center' });
  });
  yPos += cardHeight + 4;

  // SLA Performance Summary - inline compact
  const slaBreachPercent = data.summary.slaBreachedPercent || 0;
  doc.setFillColor(slaBreachPercent > 20 ? 254 : 240, slaBreachPercent > 20 ? 242 : 253, slaBreachPercent > 20 ? 242 : 244);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 10, 2, 2, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  if (slaBreachPercent > 20) {
    doc.setTextColor(...dangerColor);
    doc.text('SLA: Needs Attention', margin + 3, yPos + 6);
  } else {
    doc.setTextColor(...successColor);
    doc.text('SLA: Healthy', margin + 3, yPos + 6);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(`Met: ${data.summary.slaMet} (${data.summary.slaCompliance.toFixed(1)}%)`, margin + 55, yPos + 6);
  doc.text(`Breached: ${data.summary.slaBreached} (${slaBreachPercent.toFixed(1)}%)`, margin + 100, yPos + 6);
  doc.text(`Total: ${data.summary.totalSlaApplied || (data.summary.slaMet + data.summary.slaBreached)}`, margin + 145, yPos + 6);
  yPos += 14;

  // ========== CHARTS SECTION - ALL ON PAGE 1 ==========

  // Section title - compact
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Performance Visualizations', margin, yPos);
  yPos += 5;

  // Helper to calculate scaled dimensions maintaining aspect ratio
  const calculateScaledDimensions = (
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } => {
    const aspectRatio = originalWidth / originalHeight;

    let scaledWidth = maxWidth;
    let scaledHeight = scaledWidth / aspectRatio;

    // If height exceeds max, scale down based on height
    if (scaledHeight > maxHeight) {
      scaledHeight = maxHeight;
      scaledWidth = scaledHeight * aspectRatio;
    }

    return { width: scaledWidth, height: scaledHeight };
  };

  // Capture ALL charts at once - reorganized: 2 per row, trend full width
  const allChartConfigs = [
    { id: 'chart-ticket-type', name: 'Ticket Distribution', row: 1 },
    { id: 'chart-state-bar', name: 'State Analysis', row: 1 },
    { id: 'chart-sla-gauge', name: 'SLA Performance', row: 2 },
    { id: 'chart-priority-donut', name: 'Priority Distribution', row: 2 },
    { id: 'chart-trend-line', name: 'Trend Analysis', row: 3 },
  ];

  const allChartImages: { id: string; image: CapturedImage; name: string; row: number }[] = [];
  for (const config of allChartConfigs) {
    const image = await captureElementAsImage(config.id);
    if (image) {
      allChartImages.push({ ...config, image });
    }
  }

  // Row 1: 2 charts (Ticket Type + State Bar)
  const row1Charts = allChartImages.filter(c => c.row === 1);
  if (row1Charts.length > 0) {
    const availableWidth = pageWidth - margin * 2 - 5;
    const maxChartWidth = availableWidth / 2;
    const maxChartHeight = 55;

    let maxRowHeight = 0;
    const scaledDimensions = row1Charts.map((chart) => {
      const scaled = calculateScaledDimensions(
        chart.image.width,
        chart.image.height,
        maxChartWidth,
        maxChartHeight
      );
      if (scaled.height > maxRowHeight) maxRowHeight = scaled.height;
      return scaled;
    });

    row1Charts.forEach((chart, index) => {
      const x = margin + index * (maxChartWidth + 5);
      const dims = scaledDimensions[index];
      const yOffset = (maxRowHeight - dims.height) / 2;
      try {
        doc.addImage(chart.image.dataUrl, 'PNG', x, yPos + yOffset, dims.width, dims.height);
      } catch (e) {
        console.error('Failed to add chart:', e);
      }
    });
    yPos += maxRowHeight + 5;
  }

  // Row 2: 2 charts (SLA Gauge + Priority Donut)
  const row2Charts = allChartImages.filter(c => c.row === 2);
  if (row2Charts.length > 0) {
    const availableWidth = pageWidth - margin * 2 - 5;
    const maxChartWidth = availableWidth / 2;
    const maxChartHeight = 55;

    let maxRowHeight = 0;
    const scaledDimensions = row2Charts.map((chart) => {
      const scaled = calculateScaledDimensions(
        chart.image.width,
        chart.image.height,
        maxChartWidth,
        maxChartHeight
      );
      if (scaled.height > maxRowHeight) maxRowHeight = scaled.height;
      return scaled;
    });

    row2Charts.forEach((chart, index) => {
      const x = margin + index * (maxChartWidth + 5);
      const dims = scaledDimensions[index];
      const yOffset = (maxRowHeight - dims.height) / 2;
      try {
        doc.addImage(chart.image.dataUrl, 'PNG', x, yPos + yOffset, dims.width, dims.height);
      } catch (e) {
        console.error('Failed to add chart:', e);
      }
    });
    yPos += maxRowHeight + 5;
  }

  // Row 3: Trend chart - full width
  const row3Charts = allChartImages.filter(c => c.row === 3);
  if (row3Charts.length > 0) {
    const chart = row3Charts[0];
    const availableWidth = pageWidth - margin * 2;
    const maxChartHeight = 60;

    const dims = calculateScaledDimensions(
      chart.image.width,
      chart.image.height,
      availableWidth,
      maxChartHeight
    );

    // Center the chart horizontally
    const xOffset = (availableWidth - dims.width) / 2;
    try {
      doc.addImage(chart.image.dataUrl, 'PNG', margin + xOffset, yPos, dims.width, dims.height);
    } catch (e) {
      console.error('Failed to add trend chart:', e);
    }
    yPos += dims.height + 5;
  }

  // ========== NEW PAGE: INSIGHTS & RECOMMENDATIONS ==========

  doc.addPage();
  yPos = 20;

  // Insights Section
  drawSectionHeader('Key Insights & Analysis');

  const insights = generateInsights(data);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  insights.forEach((insight, _index) => {
    checkNewPage(15);

    // Determine icon color based on content
    let bulletColor: [number, number, number] = primaryColor;
    if (insight.toLowerCase().includes('critical') || insight.toLowerCase().includes('warning')) {
      bulletColor = dangerColor;
    } else if (insight.toLowerCase().includes('positive') || insight.toLowerCase().includes('excellent')) {
      bulletColor = successColor;
    } else if (insight.toLowerCase().includes('high') || insight.toLowerCase().includes('review')) {
      bulletColor = warningColor;
    }

    // Bullet point
    doc.setFillColor(...bulletColor);
    doc.circle(margin + 2, yPos - 1, 1.5, 'F');

    // Text with word wrap
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(insight, pageWidth - margin * 2 - 10);
    doc.text(lines, margin + 8, yPos);
    yPos += lines.length * 5 + 5;
  });

  yPos += 10;
  checkNewPage(60);

  // Recommendations Section
  drawSectionHeader('Recommended Actions');

  const recommendations = generateRecommendations(data);

  recommendations.forEach((rec, index) => {
    checkNewPage(15);

    // Action number box
    doc.setFillColor(...primaryColor);
    doc.roundedRect(margin, yPos - 4, 8, 6, 1, 1, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(String(index + 1), margin + 4, yPos, { align: 'center' });

    // Recommendation text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(rec, pageWidth - margin * 2 - 15);
    doc.text(lines, margin + 12, yPos);
    yPos += lines.length * 5 + 6;
  });

  // ========== NEW PAGE: DETAILED DATA ==========

  doc.addPage();
  yPos = 20;

  drawSectionHeader('Detailed Data Tables');

  // State Distribution Table
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('State Distribution', margin, yPos);
  yPos += 5;

  const stateLabels: Record<string, string> = {
    new: 'New', in_progress: 'In Progress', on_hold: 'On Hold',
    resolved: 'Resolved', closed: 'Closed', cancelled: 'Cancelled',
  };
  const stateTotal = Object.values(data.byState).reduce((sum, val) => sum + val, 0);
  const stateData = Object.entries(data.byState).map(([key, value]) => [
    stateLabels[key] || key, value.toString(),
    stateTotal > 0 ? `${((value / stateTotal) * 100).toFixed(1)}%` : '0%',
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['State', 'Count', '%']],
    body: stateData,
    theme: 'striped',
    headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    margin: { left: margin },
    tableWidth: 80,
  });

  // Priority table on the right
  const priorityLabels: Record<string, string> = {
    critical_1: 'Critical', high_2: 'High', moderate_3: 'Moderate',
    low_4: 'Low', planning_5: 'Planning',
  };
  const priorityTotal = Object.values(data.byPriority).reduce((sum, val) => sum + val, 0);
  const priorityData = Object.entries(data.byPriority).map(([key, value]) => [
    priorityLabels[key] || key, value.toString(),
    priorityTotal > 0 ? `${((value / priorityTotal) * 100).toFixed(1)}%` : '0%',
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Priority', 'Count', '%']],
    body: priorityData,
    theme: 'striped',
    headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    margin: { left: margin + 100 },
    tableWidth: 80,
  });

  yPos = Math.max((doc as any).lastAutoTable.finalY, yPos) + 15;

  // Trend Data
  if (data.trend.length > 0) {
    checkNewPage(80);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Daily Trend Summary', margin, yPos);
    yPos += 5;

    const trendData = data.trend.slice(-14).map((row) => {
      const [year, month, day] = row.date.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      const total = (row.cases || 0) + row.incidents + row.serviceRequests + row.changes + row.problems;
      return [
        localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        (row.cases || 0).toString(), row.incidents.toString(), row.serviceRequests.toString(),
        row.changes.toString(), row.problems.toString(), total.toString(),
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Cases', 'Inc', 'SR', 'Chg', 'Prb', 'Total']],
      body: trendData,
      theme: 'striped',
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 7 },
      bodyStyles: { fontSize: 7 },
      margin: { left: margin, right: margin },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  // ========== CUSTOMER x ASSIGNMENT GROUP MATRIX ==========

  if (matrixData && matrixData.matrix.length > 0 && matrixData.groups.length > 0) {
    checkNewPage(80);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer vs Assignment Group Matrix', margin, yPos);
    yPos += 5;

    // Build header row with group names
    const headerRow = ['Customer', ...matrixData.groups.map(g =>
      g.name.length > 12 ? g.name.substring(0, 10) + '...' : g.name
    )];

    // Build data rows
    const matrixTableData = matrixData.matrix.map(row => {
      const customerCol = row.customerName.length > 20
        ? row.customerName.substring(0, 18) + '...'
        : row.customerName;
      const dataCols = row.data.map(cell => cell.count.toString());
      return [customerCol, ...dataCols];
    });

    // Calculate cell colors based on count
    const getCellColor = (count: number): [number, number, number] | null => {
      if (count === 0) return null;
      const intensity = Math.min(count / matrixData.maxCount, 1);
      if (intensity <= 0.2) return [254, 226, 226]; // red-100
      if (intensity <= 0.4) return [254, 202, 202]; // red-200
      if (intensity <= 0.6) return [252, 165, 165]; // red-300
      if (intensity <= 0.8) return [248, 113, 113]; // red-400
      return [220, 38, 38]; // red-600
    };

    autoTable(doc, {
      startY: yPos,
      head: [headerRow],
      body: matrixTableData,
      theme: 'grid',
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 6, halign: 'center' },
      bodyStyles: { fontSize: 6, halign: 'center' },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        // Apply heatmap colors to data cells (not header, not first column)
        if (data.section === 'body' && data.column.index > 0) {
          const count = parseInt(data.cell.raw as string, 10) || 0;
          const color = getCellColor(count);
          if (color) {
            data.cell.styles.fillColor = color;
            // Use white text for darker colors
            if (count / matrixData.maxCount > 0.6) {
              data.cell.styles.textColor = [255, 255, 255];
            }
          }
        }
      }
    });
  }

  // ========== FOOTER ON ALL PAGES ==========

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Footer line
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

    doc.setFontSize(7);
    doc.setTextColor(...grayColor);
    doc.text('Pulse ITSM Dashboard Report', margin, pageHeight - 10);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text('Confidential', pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // Generate and download
  const timestamp = new Date().toISOString().split('T')[0];
  const safeCustomerName = customerName.replace(/[^a-zA-Z0-9]/g, '-');
  const filename = `Pulse-Dashboard-${safeCustomerName}-${timestamp}.pdf`;

  doc.save(filename);
};
